import { ReactNode, useEffect, useState } from "react";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { motion, AnimatePresence } from "framer-motion";
import { LiveClock } from "./LiveClock";
import { XREnterButton } from "./spatial/XREnterButton";

const NAV = [
  { href: "#presale", label: "Presale" },
  { href: "#tokenomics", label: "Tokenomics" },
  { href: "#staking", label: "Staking" },
  { href: "#roadmap", label: "Roadmap" },
  { href: "#trust", label: "Trust" },
];

export default function Layout({ children }: { children: ReactNode }) {
  const [scrolled, setScrolled] = useState(false);
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="relative min-h-screen">
      <div className="ambient" />

      <header
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled
            ? "border-b border-white/[0.06] bg-background/70 backdrop-blur-2xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 md:px-8">
          <a href="#top" className="group flex items-center gap-3">
            <div className="relative grid h-10 w-10 place-items-center overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-accent to-accent-deep shadow-magnet">
              <span className="font-display text-2xl text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.6)]">
                F
              </span>
              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-display text-2xl tracking-wide text-white">$FANZ</span>
              <span className="hidden text-[10px] uppercase tracking-[0.18em] text-white/40 md:inline">
                Creator Economy Token
              </span>
            </div>
          </a>

          <nav className="hidden items-center gap-1 md:flex">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} className="btn-ghost">
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <div className="hidden items-center gap-2 lg:flex">
              <span className="pill text-success">
                <span className="live-dot text-success" />
                Base Sepolia
              </span>
              <LiveClock />
            </div>
            <XREnterButton />
            <ConnectButton accountStatus={{ smallScreen: "avatar", largeScreen: "full" }} chainStatus="icon" />
            <button
              onClick={() => setDrawer((s) => !s)}
              className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-white/[0.04] md:hidden"
              aria-label="Open menu"
            >
              <div className="flex flex-col gap-1.5">
                <span className={`block h-0.5 w-5 bg-white transition-transform ${drawer ? "translate-y-2 rotate-45" : ""}`} />
                <span className={`block h-0.5 w-5 bg-white transition-opacity ${drawer ? "opacity-0" : ""}`} />
                <span className={`block h-0.5 w-5 bg-white transition-transform ${drawer ? "-translate-y-2 -rotate-45" : ""}`} />
              </div>
            </button>
          </div>
        </div>

        <AnimatePresence>
          {drawer && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden border-t border-white/[0.06] bg-background/95 backdrop-blur-2xl md:hidden"
            >
              <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3">
                {NAV.map((n) => (
                  <a
                    key={n.href}
                    href={n.href}
                    onClick={() => setDrawer(false)}
                    className="rounded-xl px-3 py-3 text-base text-white/80 hover:bg-white/[0.06]"
                  >
                    {n.label}
                  </a>
                ))}
                <div className="mt-2 flex items-center gap-2 px-3">
                  <span className="pill text-success">
                    <span className="live-dot text-success" />
                    Base Sepolia
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main id="top" className="relative z-10">
        {children}
      </main>
    </div>
  );
}
